import { useCallback, useEffect, useRef, useState } from "react";

// Renderer side of the case dashboard. Everything goes through
// window.electronAPI.caseDashboard (electron/caseDashboard.js): the renderer
// only ever passes case ids, allowlisted commands and named queries.

const api = () => window.electronAPI?.caseDashboard;

/** Saved cases + add/remove. */
export function useCaseList() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    const res = await api()?.list();
    if (res?.ok) setCases(res.cases);
    setLoading(false);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const add = useCallback(
    async (input) => {
      const res = await api().add(input);
      if (res?.ok) await reload();
      return res;
    },
    [reload]
  );

  const remove = useCallback(
    async (caseId) => {
      await api().remove(caseId);
      await reload();
    },
    [reload]
  );

  return { cases, loading, reload, add, remove };
}

/**
 * Connect to a case while `caseId` is set; disconnect when it changes or the
 * caller unmounts. Returns { state, error } where state is connecting |
 * connected | reconnecting | disconnected | error.
 */
export function useCaseConnection(caseId) {
  const [conn, setConn] = useState({ state: "disconnected", error: null });

  useEffect(() => {
    if (!caseId || !api()) return undefined;
    const off = api().onStatus((s) => {
      if (s.caseId === caseId) setConn({ state: s.state, error: s.error });
    });
    setConn({ state: "connecting", error: null });
    api()
      .connect(caseId)
      .then((res) => {
        if (res?.ok) setConn({ state: "connected", error: null });
        else setConn({ state: "error", error: res?.error || null });
      });
    return () => {
      off();
      api().disconnect(caseId);
    };
  }, [caseId]);

  const retry = useCallback(async () => {
    setConn({ state: "connecting", error: null });
    const res = await api().connect(caseId);
    setConn(
      res?.ok
        ? { state: "connected", error: null }
        : { state: "error", error: res?.error || null }
    );
  }, [caseId]);

  return { ...conn, retry };
}

/**
 * Run `load` now and every `intervalMs` while `enabled`. `load` resolves to
 * { ok, data, error?, missing? }. Keeps the last good data across failures.
 */
export function usePoll(load, intervalMs, deps, enabled = true) {
  const [state, setState] = useState({
    data: null,
    error: null,
    missing: false,
    loading: true,
    updatedAt: null,
  });
  const loadRef = useRef(load);
  loadRef.current = load;
  const busy = useRef(false);

  const refresh = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      const res = await loadRef.current();
      setState((prev) =>
        res?.ok
          ? {
              data: res.data,
              error: null,
              missing: !!res.missing,
              loading: false,
              updatedAt: Date.now(),
            }
          : {
              ...prev,
              error: res?.error?.message || res?.error || "error",
              loading: false,
            }
      );
    } finally {
      busy.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    if (!intervalMs) return undefined;
    const id = setInterval(refresh, intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs, refresh, ...deps]);

  return { ...state, refresh };
}

/** A named query, polled. */
export function useCaseQuery(caseId, name, params, intervalMs, enabled = true) {
  const key = JSON.stringify(params || {});
  return usePoll(
    () => api().query(caseId, name, params || {}),
    intervalMs,
    [caseId, name, key],
    enabled && !!caseId
  );
}

/** A `@@JSON` command (status, health), polled; data is the parsed JSON. */
export function useCaseCommand(caseId, command, intervalMs, enabled = true) {
  return usePoll(
    async () => {
      const res = await api().run(caseId, command);
      if (!res) return { ok: false, error: "no response" };
      return res.json
        ? { ok: true, data: res.json }
        : { ok: false, error: res.error || "no result" };
    },
    intervalMs,
    [caseId, command],
    enabled && !!caseId
  );
}

/**
 * A command whose output streams in (start/stop/restart/mode, logs).
 * start(command) resolves with the final { ok, json, lines, error } result.
 * `dropped` counts lines trimmed off the front (or cleared) since start, so
 * `dropped + index` numbers a line for as long as it's kept.
 */
export function useCaseStream(caseId, { maxLines = 2000 } = {}) {
  const [buf, setBuf] = useState({ lines: [], dropped: 0 });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const streamRef = useRef(null);
  const resolver = useRef(null);

  useEffect(() => {
    if (!api()) return undefined;
    return api().onStreamLine((msg) => {
      if (msg.streamId !== streamRef.current) return;
      if (msg.done) {
        streamRef.current = null;
        setRunning(false);
        setResult(msg.result);
        resolver.current?.(msg.result);
        resolver.current = null;
        return;
      }
      setBuf((prev) => {
        const next =
          prev.lines.length >= maxLines
            ? prev.lines.slice(-maxLines + 1)
            : prev.lines.slice();
        next.push(msg.line);
        return {
          lines: next,
          dropped: prev.dropped + (prev.lines.length + 1 - next.length),
        };
      });
    });
  }, [maxLines]);

  const start = useCallback(
    async (command) => {
      setBuf({ lines: [], dropped: 0 });
      setResult(null);
      setRunning(true);
      const id = `${command}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;
      streamRef.current = id;
      const done = new Promise((resolve) => {
        resolver.current = resolve;
      });
      const res = await api().streamStart(caseId, command, id);
      if (!res?.ok) {
        streamRef.current = null;
        setRunning(false);
        const failed = { ok: false, error: res?.error?.message || "error" };
        setResult(failed);
        resolver.current = null;
        return failed;
      }
      return done;
    },
    [caseId]
  );

  const stop = useCallback(() => {
    if (streamRef.current) api().streamStop(caseId, streamRef.current);
  }, [caseId]);

  const clear = useCallback(
    () =>
      setBuf((prev) => ({
        lines: [],
        dropped: prev.dropped + prev.lines.length,
      })),
    []
  );

  // Stop a still-running stream (e.g. logs) when the caller goes away.
  useEffect(
    () => () => {
      if (streamRef.current) api()?.streamStop(caseId, streamRef.current);
    },
    [caseId]
  );

  return {
    lines: buf.lines,
    dropped: buf.dropped,
    running,
    result,
    start,
    stop,
    clear,
  };
}
